import type { CliInsightVariableImportRequest } from '@modules/shared-types';
/**
 * InsightVariableGenerationService
 *
 * CRUD for insight variable generation sessions and their generated variables.
 * Mirrors ScrapeAnalysisService pattern.
 *
 * Path: projects/{projectId}/insightVariableGenerations/{generationId}
 * Subcollection: generatedVariables/{variableId}
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  limit,
  onSnapshot,
  writeBatch,
  Unsubscribe,
} from 'firebase/firestore';
import { CoreFirebaseService } from './CoreFirebaseService';
import { getBlockLibraryCliApiBaseUrl } from '@modules/firebase-core/services/BlockLibraryMutationApi';

// ── Types ──────────────────────────────────────────────────────────

export interface InsightVariableGenerationData {
  id: string;
  status: 'running' | 'completed' | 'reviewed' | 'applied' | 'error';
  analysisId: string;
  analysisName?: string;
  categories: string[];
  taskScope: 'global' | 'task';
  taskId?: string;
  explanation?: {
    overallStrategy: string;
    identificationFlow: string;
    confidenceEstimate: string;
    perVariable: Array<Record<string, unknown>>;
  };
  backendTaskId?: string;
  userId: string;
  provider?: string;
  model?: string;
  error?: string;
  createdAt: string | { seconds: number; nanoseconds: number };
  updatedAt?: string | { seconds: number; nanoseconds: number };
}

export interface GeneratedVariableEntityData {
  id: string;
  entityType: 'variable';
  data: {
    variable: Record<string, unknown>;
    optionMappings: Array<Record<string, unknown>>;
    explanation: Record<string, unknown>;
    dataUsage?: { items: Array<Record<string, unknown>> };
  };
  accepted: boolean | null;
  createdAt: string;
}

// ── Service ────────────────────────────────────────────────────────

export class InsightVariableGenerationService {
  private readonly firestore = CoreFirebaseService.getInstance().firestore;
  private readonly projectId: string;
  private readonly importRequests = new Map<string, string>();

  constructor(projectId: string) {
    this.projectId = projectId;
  }

  private get collectionRef() {
    return collection(this.firestore, 'projects', this.projectId, 'insightVariableGenerations');
  }

  // ── Generation CRUD ──────────────────────────────────────────────

  async get(id: string): Promise<InsightVariableGenerationData | null> {
    try {
      const docRef = doc(this.firestore, 'projects', this.projectId, 'insightVariableGenerations', id);
      const snap = await getDoc(docRef);
      return snap.exists() ? { ...snap.data(), id: snap.id } as InsightVariableGenerationData : null;
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'InsightVariableGenerationService.get');
      throw error;
    }
  }

  async list(options: { limit?: number } = {}): Promise<InsightVariableGenerationData[]> {
    try {
      const q = query(this.collectionRef, orderBy('createdAt', 'desc'), limit(options.limit || 20));
      const snap = await getDocs(q);
      return snap.docs.map(d => ({ ...d.data(), id: d.id } as InsightVariableGenerationData));
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'InsightVariableGenerationService.list');
      throw error;
    }
  }

  async delete(id: string): Promise<void> {
    try {
      await deleteDoc(doc(this.firestore, 'projects', this.projectId, 'insightVariableGenerations', id));
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'InsightVariableGenerationService.delete');
      throw error;
    }
  }

  subscribe(
    onData: (items: InsightVariableGenerationData[]) => void,
    onError?: (error: Error) => void,
  ): Unsubscribe {
    const q = query(this.collectionRef, orderBy('createdAt', 'desc'));
    return onSnapshot(q, (snap) => {
      onData(snap.docs.map(d => ({ ...d.data(), id: d.id } as InsightVariableGenerationData)));
    }, onError);
  }

  subscribeToGeneration(
    generationId: string,
    onData: (generation: InsightVariableGenerationData | null) => void,
    onError?: (error: Error) => void,
  ): Unsubscribe {
    const docRef = doc(this.firestore, 'projects', this.projectId, 'insightVariableGenerations', generationId);
    return onSnapshot(docRef, (snap) => {
      onData(snap.exists() ? { ...snap.data(), id: snap.id } as InsightVariableGenerationData : null);
    }, onError);
  }

  // ── Generated Variables (subcollection) ──────────────────────────

  private variablesCollectionRef(generationId: string) {
    return collection(
      this.firestore, 'projects', this.projectId,
      'insightVariableGenerations', generationId, 'generatedVariables',
    );
  }

  async listVariables(generationId: string): Promise<GeneratedVariableEntityData[]> {
    try {
      const q = query(this.variablesCollectionRef(generationId), orderBy('createdAt', 'asc'));
      const snap = await getDocs(q);
      return snap.docs.map(d => ({ ...d.data(), id: d.id } as GeneratedVariableEntityData));
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'InsightVariableGenerationService.listVariables');
      throw error;
    }
  }

  async updateVariableAccepted(generationId: string, variableId: string, accepted: boolean): Promise<void> {
    try {
      const docRef = doc(
        this.firestore, 'projects', this.projectId,
        'insightVariableGenerations', generationId, 'generatedVariables', variableId,
      );
      await updateDoc(docRef, { accepted });
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'InsightVariableGenerationService.updateVariableAccepted');
      throw error;
    }
  }

  async bulkUpdateAccepted(
    generationId: string,
    updates: Array<{ variableId: string; accepted: boolean }>,
  ): Promise<void> {
    try {
      const batch = CoreFirebaseService.getInstance().batch();
      for (const { variableId, accepted } of updates) {
        const docRef = doc(
          this.firestore, 'projects', this.projectId,
          'insightVariableGenerations', generationId, 'generatedVariables', variableId,
        );
        batch.update(docRef, { accepted });
      }
      await batch.commit();
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'InsightVariableGenerationService.bulkUpdateAccepted');
      throw error;
    }
  }

  subscribeToVariables(
    generationId: string,
    onData: (variables: GeneratedVariableEntityData[]) => void,
    onError?: (error: Error) => void,
  ): Unsubscribe {
    const q = query(this.variablesCollectionRef(generationId), orderBy('createdAt', 'asc'));
    return onSnapshot(q, (snap) => {
      onData(snap.docs.map(d => ({ ...d.data(), id: d.id } as GeneratedVariableEntityData)));
    }, onError);
  }

  // ── Status update ────────────────────────────────────────────────

  async updateStatus(generationId: string, status: InsightVariableGenerationData['status']): Promise<void> {
    try {
      const docRef = doc(this.firestore, 'projects', this.projectId, 'insightVariableGenerations', generationId);
      await updateDoc(docRef, { status, updatedAt: new Date().toISOString() });
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'InsightVariableGenerationService.updateStatus');
      throw error;
    }
  }

  // ── Import generation (from skill eval or external source) ──────

  /**
   * Import an InsightVariableResult into generation history.
   * Creates the same Firestore structure as InsightVariableHandler Phase 4,
   * so the standard review/apply flow works identically.
   */
  async importGeneration(params: Omit<CliInsightVariableImportRequest, 'requestId'>): Promise<string> {
    try {
      const user = CoreFirebaseService.getInstance().auth.currentUser;
      if (!user) throw new Error('Sign in before importing insight variables.');
      const base = getBlockLibraryCliApiBaseUrl().replace(/\/$/, '');
      const capabilityResponse = await fetch(`${base}/api/cli/v1/capabilities`);
      const capabilities = await capabilityResponse.json();
      if (!capabilityResponse.ok || capabilities?.success === false || capabilities?.data?.features?.transactionalInsightVariableImport !== true) {
        throw new Error('The server does not support protected variable import yet. Update the server before retrying.');
      }
      const key = JSON.stringify({ userId: user.uid, params });
      const requestId = this.importRequests.get(key) ?? doc(this.collectionRef).id;
      this.importRequests.set(key, requestId); // Retain after an uncertain response for an idempotent retry.
      const token = await user.getIdToken();
      const response = await fetch(`${base}/api/cli/v1/projects/${encodeURIComponent(this.projectId)}/product/variables/import`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...params, requestId }),
      });
      const payload = await response.json();
      if (!response.ok || payload?.success === false) throw new Error(payload?.detail ?? payload?.error?.message ?? 'Variable import failed.');
      const receipt = payload?.data;
      if (receipt?.projectId !== this.projectId || receipt?.generationId !== `import_${requestId}`
        || receipt?.importPolicyVersion !== 'transactional-import-v1' || receipt?.variableCount !== params.variables.length
        || typeof receipt?.inputHash !== 'string' || !/^[a-f0-9]{64}$/.test(receipt.inputHash)) {
        throw new Error('Variable import returned an invalid receipt. Refresh history before retrying.');
      }
      const expectedIds = new Set(params.variables.map(row => row.variable.id));
      const [generation, variables] = await Promise.all([this.get(receipt.generationId), this.listVariables(receipt.generationId)]);
      if (!generation || (generation as any).importReceipt?.inputHash !== receipt.inputHash
        || variables.length !== receipt.variableCount || variables.some(row => row.id !== row.data.variable.id || !expectedIds.has(row.id))
        || new Set(variables.map(row => row.id)).size !== variables.length) {
        throw new Error('Imported history could not be verified. Refresh history before retrying.');
      }
      return receipt.generationId;
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'InsightVariableGenerationService.importGeneration');
      throw error;
    }
  }
}
